import { useMelioIntl } from '@melio/platform-i18n';
import { Features } from '@melio/subscriptions-api';

const contextualCurrentPlanDetails = {
  collaborators: {
    capabilities: [
      `activities.subscription.contextualPlans.collaborators.current.capabilities.1teamMember`,
      `activities.subscription.contextualPlans.collaborators.current.capabilities.ownerRole`,
    ],
  },
  batchPayments: {
    capabilities: [
      `activities.subscription.contextualPlans.batchPayments.current.capabilities.paySingleBills`,
      `activities.subscription.contextualPlans.batchPayments.current.capabilities.noCombinedPayments`,
    ],
    actionButton: `activities.subscription.contextualPlans.batchPayments.current.actionButtonLabel`,
  },
  accountingPlatformQbo: {
    capabilities: [
      `activities.subscription.contextualPlans.accountingPlatformQbo.current.capabilities.1teamMember`,
      `activities.subscription.contextualPlans.accountingPlatformQbo.current.capabilities.basicCapabilities`,
    ],
  },
  accountingPlatformXero: {
    capabilities: [
      `activities.subscription.contextualPlans.accountingPlatformXero.current.capabilities.1teamMember`,
      `activities.subscription.contextualPlans.accountingPlatformXero.current.capabilities.basicCapabilities`,
    ],
  },
} as const;

const contextualUpgradePlanDetails = {
  collaborators: {
    capabilities: [
      'activities.subscription.contextualPlans.collaborators.upgrade.capabilities.unlimitedCollaborators',
      'activities.subscription.contextualPlans.collaborators.upgrade.capabilities.mutliplerRoles',
    ],
  },
  batchPayments: {
    capabilities: [
      `activities.subscription.contextualPlans.batchPayments.upgrade.capabilities.mutlipleBills`,
      `activities.subscription.contextualPlans.batchPayments.upgrade.capabilities.combinedBillPayments`,
    ],
  },
  accountingPlatformQbo: {
    capabilities: [
      `activities.subscription.contextualPlans.accountingPlatformQbo.upgrade.capabilities.advanceAccountingCapabilities`,
      `activities.subscription.contextualPlans.accountingPlatformQbo.upgrade.capabilities.directAccountingIntegrations`,
      `activities.subscription.contextualPlans.accountingPlatformQbo.upgrade.capabilities.multipleTeamMembers`,
      `activities.subscription.contextualPlans.accountingPlatformQbo.upgrade.capabilities.quickbooksAndXero`,
    ],
  },
  accountingPlatformXero: {
    capabilities: [
      `activities.subscription.contextualPlans.accountingPlatformXero.upgrade.capabilities.advanceAccountingCapabilities`,
      `activities.subscription.contextualPlans.accountingPlatformXero.upgrade.capabilities.directAccountingIntegrations`,
      `activities.subscription.contextualPlans.accountingPlatformXero.upgrade.capabilities.multipleTeamMembers`,
      `activities.subscription.contextualPlans.accountingPlatformXero.upgrade.capabilities.quickbooksAndXero`,
    ],
  },
} as const;

type UseContextualMessagesProps = {
  featureName: keyof Features;
  upgradePlanName: string;
};

type FeatureUpsellContext = {
  title: string;
  description: string;
  plans: {
    current: {
      capabilities: string[];
      description: string;
      actionButtonLabel?: string;
    };
    upgrade: {
      capabilities: string[];
      description: string;
      actionButtonLabel: string;
    };
  };
};

// TODO: part of the texts here should be dynamic - the eligacility for trial and the next plan (plus or pro). for now, all of them are static https://meliorisk.atlassian.net/browse/ME-54165
export const useContextualMessages = ({
  featureName,
  upgradePlanName,
}: UseContextualMessagesProps): FeatureUpsellContext => {
  const { formatMessage } = useMelioIntl();

  const currentPlanDetails = contextualCurrentPlanDetails[featureName];
  const currentPlanTexts = {
    description: formatMessage(`activities.subscription.contextualPlans.${featureName}.current.description`),
    actionButtonLabel:
      'actionButton' in currentPlanDetails ? formatMessage(currentPlanDetails.actionButton) : undefined,
    capabilities: currentPlanDetails.capabilities.map((x) => formatMessage(x)),
  };

  const upgradePlanDetails = contextualUpgradePlanDetails[featureName];
  const upgradePlanTexts = {
    description: formatMessage(`activities.subscription.contextualPlans.${featureName}.upgrade.description`),
    // TODO: https://meliorisk.atlassian.net/browse/ME-54886 text should be different if eligible for trial
    actionButtonLabel: formatMessage('activities.subscription.contextualPlans.action.upgradeTo', {
      planName: upgradePlanName,
    }),
    capabilities: upgradePlanDetails.capabilities.map((x) => formatMessage(x)),
  };

  return {
    title: formatMessage(`activities.subscription.contextualPlans.${featureName}.title`, { upgradePlanName }),
    description: formatMessage(`activities.subscription.contextualPlans.${featureName}.description`),
    plans: {
      current: currentPlanTexts,
      upgrade: upgradePlanTexts,
    },
  };
};
